import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, Chip, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import CakeIcon from '@mui/icons-material/Cake';
import WorkIcon from '@mui/icons-material/Work';
import PhoneIcon from '@mui/icons-material/Phone';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import ScienceIcon from '@mui/icons-material/Science';

const InfoField = ({ label, value, icon: Icon, chipColor }) => {
  const theme = useTheme();
  const secondaryColor = theme.palette.secondary?.main || '#7b809a';
  const textColor = theme.palette.text?.main || '#344767';

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
        {Icon && <Icon sx={{ fontSize: 14, color: secondaryColor }} />}
        <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
      </Box>
      {chipColor ? (
        <Chip label={value || 'N/A'} size="small" sx={{ backgroundColor: chipColor, color: '#fff', fontWeight: 600, fontSize: '0.8rem' }} />
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600, color: textColor, fontSize: '0.875rem' }}>
          {value || 'N/A'}
        </Typography>
      )}
    </Box>
  );
};

function PatientInfoCard({ patientData, infertilityData, bloodGroup }) {
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';
  const textColor = theme.palette.text?.main || '#344767';
  const secondaryColor = theme.palette.secondary?.main || '#7b809a';

  if (!patientData) return null;

  const {
    full_name, first_name, last_name,
    date_of_birth, age, occupation,
    phone_number, email_address,
    marital_status, married_for, trying_for,
    gender,
  } = patientData;

  const name = full_name || `${first_name || ''} ${last_name || ''}`.trim();

  const calculateAge = (dob) => {
    if (!dob) return age || 'N/A';
    const today = new Date();
    const birthDate = new Date(dob);
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    return calculatedAge;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const showMarriageInfo = marital_status === 'married' || marital_status === 'widowed' || marital_status === 'widow';

  const formatInfertilityType = (type) => {
    if (!type) return null;
    const typeMap = {
      'primary': 'Primary Infertility',
      'secondary': 'Secondary Infertility',
      'boh': 'BOH',
      'gynecological': 'Gynecological Problems',
    };
    return typeMap[type.toLowerCase()] || type;
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label="Name" value={name} icon={PersonIcon} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <InfoField label="Date of Birth" value={formatDate(date_of_birth)} icon={CakeIcon} />
        </Grid>
        <Grid item xs={12} sm={6} md={1}>
          <InfoField label="Age" value={calculateAge(date_of_birth)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label="Occupation" value={occupation} icon={WorkIcon} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <InfoField label="Contact" value={phone_number} icon={PhoneIcon} />
        </Grid>
      </Grid>

      {showMarriageInfo && (
        <>
          <Divider sx={{ my: 1.5, borderColor: '#e9ecef' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <InfoField label="Duration of Marriage" value={married_for ? `${married_for}` : 'N/A'} icon={FavoriteIcon} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoField label="Trying For" value={trying_for ? `${trying_for}` : 'N/A'} />
            </Grid>
            {infertilityData && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ mb: 1.5 }}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                      <ScienceIcon sx={{ fontSize: 14, color: secondaryColor }} />
                      <Typography variant="caption" sx={{ color: secondaryColor, fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Infertility Type [{infertilityData.para_p1 || '0'}+{infertilityData.para_p2 || '0'}]
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textColor, fontSize: '0.875rem' }}>
                      {formatInfertilityType(infertilityData.infetrtility_type) || 'N/A'}
                    </Typography>
                    {infertilityData.remarks && (
                      <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: secondaryColor, fontStyle: 'italic' }}>
                        <span dangerouslySetInnerHTML={{ __html: infertilityData.remarks }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <InfoField label="Blood Group" value={bloodGroup || 'N/A'} icon={BloodtypeIcon} chipColor={bloodGroup ? '#d32f2f' : undefined} />
            </Grid>
          </Grid>
        </>
      )}

      {!showMarriageInfo && bloodGroup && (
        <>
          <Divider sx={{ my: 1.5, borderColor: '#e9ecef' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <InfoField label="Blood Group" value={bloodGroup} icon={BloodtypeIcon} chipColor="#d32f2f" />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

InfoField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.elementType,
  chipColor: PropTypes.string,
};

PatientInfoCard.propTypes = {
  patientData: PropTypes.shape({
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    date_of_birth: PropTypes.string,
    age: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    occupation: PropTypes.string,
    phone_number: PropTypes.string,
    email_address: PropTypes.string,
    marital_status: PropTypes.string,
    married_for: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trying_for: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    gender: PropTypes.string,
  }),
  infertilityData: PropTypes.shape({
    infetrtility_type: PropTypes.string,
    para_p1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    para_p2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    remarks: PropTypes.string,
  }),
  bloodGroup: PropTypes.string,
};

export default PatientInfoCard;
